import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { User, Phone, Mail, Lock, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import BranchPermissionSections from "./BranchPermissionSections";
import { POST } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";
import { t } from "../../locales";
import { route } from "../../static/route";
import { useAppSelector } from "../../app/hooks";
import {
  getPermissions,
  getAllBranches,
} from "../../features/users/usersSlice";
import { UserBranchFormData } from "../../types/users";

type FormData = {
  name: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const UserCreateForm = () => {
  const navigate = useNavigate();
  const permissions = useAppSelector(getPermissions);
  const allBranches = useAppSelector(getAllBranches);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [branchData, setBranchData] = useState<UserBranchFormData[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const payload = {
        name: data.name,
        phone: data.phone,
        email: data.email,
        password: data.password,
        branches: branchData.map((b) => ({
          branch_id: b.branchId,
          permissions: b.permissions,
        })),
      };

      const response: any = await POST("/v1/users", payload);
      if (response.data.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        navigate(route.users);
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
        <CardContent className="pt-6 space-y-6">
          {/* Fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#113F67]">
                {t.users.nameLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <Input
                  className={`pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.name
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                  placeholder={t.users.namePlaceholder}
                  {...register("name", { required: t.users.nameRequired })}
                />
                {errors.name && (
                  <AlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#113F67]">
                {t.users.phoneLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <Input
                  className={`pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.phone
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                  placeholder={t.users.phonePlaceholder}
                  {...register("phone", { required: t.users.phoneRequired })}
                />
                {errors.phone && (
                  <AlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>
              {errors.phone && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#113F67]">
                {t.users.emailLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <Input
                  className={`pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                  placeholder={t.users.emailPlaceholder}
                  {...register("email", { required: t.users.emailRequired })}
                />
                {errors.email && (
                  <AlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#113F67]">
                {t.users.passwordLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <Input
                  className={`pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.password
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                  type={showPassword ? "text" : "password"}
                  placeholder={t.users.passwordPlaceholder}
                  {...register("password", {
                    required: t.users.passwordRequired,
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-4 text-[#34699A] hover:text-[#113F67]"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#113F67]">
                {t.users.confirmPasswordLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <Input
                  className={`pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.confirmPassword
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t.users.confirmPasswordPlaceholder}
                  {...register("confirmPassword", {
                    required: t.users.confirmPasswordRequired,
                    validate: (val, formValues) =>
                      val === formValues.password ||
                      t.users.confirmPasswordMatch,
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-4 text-[#34699A] hover:text-[#113F67]"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {/* Branch Permissions */}
          {allBranches.length > 0 && (
            <BranchPermissionSections
              branches={allBranches}
              permissions={permissions}
              value={branchData}
              onChange={setBranchData}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              className="border border-[#34699A] text-[#113F67] hover:bg-[#113F67]/5 bg-white"
              onClick={() => navigate(route.users)}
              disabled={isLoading}
            >
              {t.users.cancelButton}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.users.creating}
                </>
              ) : (
                t.users.createButton
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default UserCreateForm;
